import { useState, useCallback } from 'react';

export interface ClientRecord {
  id: string;
  client_name: string;
  service_type: string;
  cr_description: string;
  dr_description: string;
  cr_photo_url: string | null; // base64 data URL
  dr_photo_url: string | null; // base64 data URL
  color_plan: Record<string, unknown> | null;
  cutting_plan: Record<string, unknown> | null;
  formula: string;
  notes: string;
  created_at: string;
}

export interface SaveClientInput {
  clientName?: string;
  serviceType: string;
  crDescription?: string;
  drDescription?: string;
  crFile?: File | null;
  drFile?: File | null;
  colorPlan?: Record<string, unknown> | null;
  cuttingPlan?: Record<string, unknown> | null;
  formula?: string;
  notes?: string;
}

const STORAGE_KEY = 'hpe_client_records';

function readRecords(): ClientRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ClientRecord[]) : [];
  } catch {
    return [];
  }
}

function writeRecords(records: ClientRecord[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useClientRecords() {
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const saveClient = async (input: SaveClientInput): Promise<string | null> => {
    setSaving(true);
    try {
      let crPhotoUrl: string | null = null;
      let drPhotoUrl: string | null = null;

      if (input.crFile) {
        try { crPhotoUrl = await fileToDataUrl(input.crFile); } catch { /* skip */ }
      }
      if (input.drFile) {
        try { drPhotoUrl = await fileToDataUrl(input.drFile); } catch { /* skip */ }
      }

      const id = crypto.randomUUID();
      const record: ClientRecord = {
        id,
        client_name: input.clientName?.trim() || 'Unnamed Client',
        service_type: input.serviceType,
        cr_description: input.crDescription || '',
        dr_description: input.drDescription || '',
        cr_photo_url: crPhotoUrl,
        dr_photo_url: drPhotoUrl,
        color_plan: input.colorPlan || null,
        cutting_plan: input.cuttingPlan || null,
        formula: input.formula || '',
        notes: input.notes || '',
        created_at: new Date().toISOString(),
      };

      const existing = readRecords();
      writeRecords([record, ...existing]);
      setRecords((prev) => [record, ...prev]);
      return id;
    } catch (err) {
      console.error('saveClient error:', err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const fetchRecords = useCallback(() => {
    setLoading(true);
    const data = readRecords();
    setRecords(data);
    setLoading(false);
  }, []);

  const deleteRecord = (id: string) => {
    const updated = readRecords().filter((r) => r.id !== id);
    writeRecords(updated);
    setRecords(updated);
  };

  const renameClient = (id: string, name: string) => {
    const updated = readRecords().map((r) =>
      r.id === id ? { ...r, client_name: name.trim() || 'Unnamed Client' } : r
    );
    writeRecords(updated);
    setRecords(updated);
  };

  const updateNotes = (id: string, notes: string) => {
    const updated = readRecords().map((r) =>
      r.id === id ? { ...r, notes } : r
    );
    writeRecords(updated);
    setRecords(updated);
  };

  return { saving, saveClient, records, loading, fetchRecords, deleteRecord, renameClient, updateNotes };
}
